import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star, Download, Package, History, Clock, HardDrive } from "lucide-react";
import { api, mediaUrl, downloadUrl } from "@/lib/api";
import { InstallGuide } from "@/components/InstallGuide";
import { Footer } from "@/components/Footer";

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

export default function AppDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    api
      .get(`/apps/${slug}`)
      .then(({ data }) => setApp(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const download = (versionId) => {
    const a = document.createElement("a");
    a.href = downloadUrl(app.id, versionId);
    a.setAttribute("download", "");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setGuide(true);
  };

  if (notFound)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#08090E]">
        <div className="text-center">
          <p className="font-display text-2xl text-slate-300">App not found</p>
          <button onClick={() => navigate("/")} className="co-btn-primary mt-4 rounded-lg px-5 py-2 text-sm" data-testid="back-store">
            Back to store
          </button>
        </div>
      </div>
    );

  if (!app)
    return <div className="flex min-h-screen items-center justify-center bg-[#08090E] font-mono text-emerald-400">loading…</div>;

  const icon = mediaUrl(app.icon_path);

  return (
    <div className="min-h-screen bg-[#08090E]">
      <header className="sticky top-0 z-40 border-b border-[#1E2433]/80 bg-[#08090E]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3 sm:px-8">
          <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-emerald-400">
            <ArrowLeft className="h-4 w-4" /> Store
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="co-grid relative overflow-hidden rounded-2xl border border-[#1E2433] bg-[#0b0d13] p-6 sm:p-8">
          <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="h-24 w-24 overflow-hidden rounded-2xl border border-[#1E2433] bg-[#10131B]">
              {icon ? (
                <img src={icon} alt={app.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-4xl font-black text-emerald-300">{app.name?.[0]}</div>
              )}
            </div>
            <div className="flex-1">
              <h1 className="font-display text-3xl font-extrabold text-slate-100 sm:text-4xl">{app.name}</h1>
              <p className="text-sm text-slate-500">{app.developer}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 font-mono text-xs">
                <span className="flex items-center gap-1 text-amber-400"><Star className="h-3.5 w-3.5 fill-amber-400" /> {app.rating?.toFixed(1)}</span>
                <span className="rounded border border-emerald-500/30 px-2 py-0.5 uppercase tracking-wider text-emerald-400">{app.category}</span>
                <span className="text-slate-500">v{app.current_version}</span>
                <span className="flex items-center gap-1 text-slate-500"><Download className="h-3.5 w-3.5" /> {app.downloads}</span>
              </div>
              <p className="mt-1 flex items-center gap-1.5 font-mono text-xs text-slate-600"><Package className="h-3.5 w-3.5" /> {app.package_id}</p>
            </div>
            <button data-testid="detail-download-btn" onClick={() => download()} className="co-btn-primary flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm">
              <Download className="h-4 w-4" /> Install v{app.current_version}
            </button>
          </div>
        </motion.div>

        {app.screenshots?.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display mb-3 text-xl font-bold text-slate-100">Screenshots</h2>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {app.screenshots.map((s, i) => (
                <img key={i} src={mediaUrl(s)} alt={`screenshot ${i + 1}`} data-testid={`screenshot-${i}`} className="h-64 shrink-0 rounded-xl border border-[#1E2433] object-cover" />
              ))}
            </div>
          </section>
        )}

        <section className="mt-8">
          <h2 className="font-display mb-3 text-xl font-bold text-slate-100">About</h2>
          <p className="whitespace-pre-line text-sm leading-relaxed text-slate-400">{app.description || "No description provided."}</p>
        </section>

        <section className="mt-8">
          <h2 className="font-display mb-3 flex items-center gap-2 text-xl font-bold text-slate-100"><History className="h-5 w-5 text-emerald-400" /> Version history</h2>
          <div className="space-y-3">
            {app.versions?.map((v, i) => (
              <div key={v.version_id} data-testid={`version-${v.version}`} className="rounded-xl border border-[#1E2433] bg-[#10131B] p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-lg font-bold text-slate-100">v{v.version}</span>
                    {i === 0 && <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-400">Latest</span>}
                    <span className="flex items-center gap-1 font-mono text-xs text-slate-500"><Clock className="h-3 w-3" /> {fmtDate(v.created_at)}</span>
                    <span className="flex items-center gap-1 font-mono text-xs text-slate-500"><HardDrive className="h-3 w-3" /> {fmtSize(v.size)}</span>
                    <span className="rounded border border-slate-700 px-1.5 py-0.5 font-mono text-[10px] uppercase text-slate-400">{v.storage_mode === "db" ? "vault" : "cloud"}</span>
                  </div>
                  <button data-testid={`version-download-${v.version}`} onClick={() => download(v.version_id)} className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs text-emerald-300 transition-colors hover:bg-emerald-500/10">
                    <Download className="h-3.5 w-3.5" /> APK
                  </button>
                </div>
                {v.changelog && <p className="mt-2 text-xs leading-relaxed text-slate-400">{v.changelog}</p>}
              </div>
            ))}
          </div>
        </section>
      </main>

      <InstallGuide open={guide} appName={app.name} onClose={() => setGuide(false)} />
      <Footer />
    </div>
  );
}
